import React, { useState } from 'react';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { format, isAfter, isValid, parseISO } from 'date-fns';
import { Controller, useFormContext } from 'react-hook-form';
import { useTheme } from '@mui/material';
import { useDebounceFn } from 'src/hooks/useDebounceFn';
import { ReactComponent as ArrowDropDownSvg } from 'src/icons/arrowDropDown.svg';
import { ReactComponent as LeftArrowIcon } from 'src/icons/leftArrowIcon.svg';
import { ReactComponent as RightArrowIcon } from 'src/icons/rightArrowIcon.svg';

import {
  calendarHeaderStyles,
  datePickerPopperStyles,
  responsiveInputStyles,
} from '../styled';
import { CalendarProps, DashboardForm } from '../types';

const ArrowDropDownIcon = (props: any) => {
  // eslint-disable-next-line no-unused-vars
  const { ownerState, ...other } = props;

  return <ArrowDropDownSvg {...other} />;
};

const CustomCalendar = ({ name, disabled }: CalendarProps) => {
  const { control, watch } = useFormContext<DashboardForm>();
  const [inputValue, setInputValue] = useState<string>(watch(name));
  const theme = useTheme();

  const shouldDisableDate = (date: Date) => {
    return isAfter(date, new Date());
  };

  const handleSave = ({
    date,
    onChange,
  }: {
    date: Date;
    onChange: (...event: any[]) => void;
  }) => {
    // Il backend lavora su date-only (YYYY-MM-DD) e gestisce il timezone lato server.
    // Inoltre, alcune stringhe ISO storiche possono avere offset "strani" (es. +00:49)
    // quando accidentalmente si finisce con anni < 1970: meglio normalizzare e validare qui.
    const y = date.getFullYear();
    if (y < 2000 || y > 2100) return;
    onChange(format(date, 'yyyy-MM-dd'));
  };

  const debFunction = useDebounceFn(handleSave, 600);

  const checkError = () => {
    const startDate = new Date(watch('startDate')).getTime();
    const endDate = new Date(watch('endDate')).getTime();

    return startDate - endDate > 0;
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { ref, onChange } }) => (
        <DesktopDatePicker
          ref={ref}
          value={parseISO(inputValue)}
          disabled={disabled}
          shouldDisableDate={shouldDisableDate}
          onChange={(date) => {
            if (isValid(date) && date) {
              const y = date.getFullYear();
              if (y < 2000 || y > 2100) return;
              setInputValue(format(date, 'yyyy-MM-dd'));
              debFunction({ date, onChange });
            }
          }}
          format="MMM dd, yyyy"
          sx={{
            minWidth: 0,
            maxWidth: '100%',
            '& .MuiInputBase-root': {
              minWidth: 0,
              maxWidth: '100%',
            },
            '& .MuiInputBase-input': {
              minWidth: 0,
              maxWidth: '100%',
            },
          }}
          dayOfWeekFormatter={(date) =>
            format(date, 'EEEE').charAt(0).toUpperCase() +
            format(date, 'EEEE').charAt(1).toLowerCase()
          }
          slots={{
            openPickerIcon: ArrowDropDownIcon,
            leftArrowIcon: LeftArrowIcon,
            rightArrowIcon: RightArrowIcon,
          }}
          slotProps={{
            calendarHeader: {
              sx: calendarHeaderStyles,
            },

            popper: {
              sx: datePickerPopperStyles,
            },
            textField: {
              InputProps: {
                sx: responsiveInputStyles(theme),
              },
              error: checkError(),
            },
          }}
        />
      )}
    />
  );
};

export default CustomCalendar;
